/**
 * CTX-M2-10 — LO QUE UN ASISTENTE CREE, Y LA DECISION DE LA PERSONA.
 *
 * POR QUE ESTA PANTALLA EXISTE, en una linea de Ares (#10785): "sin ella el
 * ciclo queda a medias: el tutor ESTIMA y nadie CONFIRMA". Toda la doctrina de
 * la casa se apoya en que el nivel de alguien lo valida una PERSONA y no un
 * modelo, y hasta hoy esa persona no tenia donde hacerlo.
 *
 * NO HUBO QUE INVENTAR NADA, y conviene decirlo porque cambia el tamano de la
 * tarea: las dos rutas ya existen en NexoAPI, y una trae en su propio docstring
 * "Es la pantalla de Josse (CTX-M2-10), no del asistente". La API se adelanto.
 * Aqui solo se consume.
 *
 * LAS DOS RUTAS, leidas en LearningContextController el 4-sep:
 *   GET  /api/v1/learning/profile?subject={uuid}&program={codigo}
 *   POST /api/v1/learning/profiles/{subjectId}/{programCode}/validate
 *
 * Y DESPLEGADAS, medido sin credenciales por codigo de estado y en PAREJA --
 * que es lo unico que prueba algo, porque un 401 suelto calza con cualquier
 * patron parecido:
 *   GET  /profile ................................ 401 (existe, gateada)
 *   GET  /perfilquenoexiste (misma forma) ........ 404
 *   POST /profiles/{guid}/{code}/validate ........ 401 (existe)
 *   POST /profiles/{guid}/{code}/verboinventado .. 404
 *   GET  /openapi.json (control del metodo) ...... 200
 *
 * LAS DOS SON [RequireManager] Y RECHAZAN LLAVES DE AGENTE con un 403 propio,
 * "validate_reserved_for_owner". Eso no es un detalle de implementacion: es la
 * regla que hace que la pantalla tenga sentido, y por eso se dice en pantalla.
 *
 * VIVE EN SU PROPIO ARCHIVO y no dentro de ContextApiClient a proposito. Aquel
 * es de CREDENCIALES; esto es del PERFIL de una persona. Son dos dominios.
 * (Y de paso: ContextApiClient esta en mi PR #274 abierto, y la regla de la
 * casa es no compartir archivos entre PRs.)
 */
import { ApiBase } from './ApiBase.js';
import { AppConfig } from '../../config.js';

const BASE = '/api/v1/learning';

const texto = (v) => (v === null || v === undefined ? '' : String(v));
const lista = (v) => (Array.isArray(v) ? v : []);

/** Primer valor no nulo entre varias claves posibles (tolera dos casings al LEER). */
const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};

/**
 * EL FALLO DICE SU CAUSA, no "algo salio mal".
 *
 * Es el mismo reparto que usa la pantalla de accesos, y esta repetido aqui por
 * una razon honesta: son mensajes DISTINTOS. Aquel habla de llaves; este habla
 * del perfil de una persona. Compartir la funcion obligaria a un texto
 * generico, que es justo lo que hace dudar de cosas que si funcionan.
 */
function motivoDe(res) {
  // SE LEE `code`, NO `status`. ApiBase devuelve fail(code, message, details) ->
  // {ok:false, error, code, details}. No hay ningun campo `status`, asi que
  // mirarlo devolveria undefined SIEMPRE y el reparto caeria al mensaje
  // generico. Lo verifique en domain/value-objects/CrudResult.js antes de
  // escribirlo, porque es exactamente el fallo de medir la forma que uno
  // recuerda en vez de la que hay.
  //
  // OJO CON EL 401: ApiBase, al recibirlo, hace logout() y recarga la pagina
  // antes de devolver nada. O sea que la rama SIN_SESION de abajo casi nunca se
  // ve: se ve la pantalla de entrar. Se deja igual porque es lo correcto si
  // algun dia ese comportamiento cambia, pero conviene saberlo.
  const txt = JSON.stringify(res?.error ?? '') + ' ' + texto(res?.code) + ' ' + JSON.stringify(res?.details ?? '');
  if (/reserved_for_owner/i.test(txt)) {
    return { motivo: 'ES_DEL_DUENO', detalle: 'validar es del dueno: una llave de asistente no puede confirmar lo que el mismo estimo' };
  }
  if (/401|unauthor|sesion|token/i.test(txt)) {
    return { motivo: 'SIN_SESION', detalle: 'tu sesion no tiene permiso para ver este perfil' };
  }
  if (/403|forbidden|out_of_scope/i.test(txt)) {
    return { motivo: 'PROHIBIDO', detalle: 'esa persona esta fuera del alcance de tu sesion' };
  }
  if (/profile_not_found|404|not\s*found/i.test(txt)) {
    return { motivo: 'SIN_PERFIL', detalle: 'no hay perfil para esa persona en ese programa todavia' };
  }
  if (/program_required/i.test(txt)) {
    return { motivo: 'FALTA_PROGRAMA', detalle: 'falta decir de que programa' };
  }
  if (AppConfig.env === 'dev') {
    return { motivo: 'DEV_SIN_API', detalle: 'el panel esta en dev y apunta a ' + AppConfig.apiBase + '; en produccion estas rutas si existen (medidas: 401, no 404)' };
  }
  return { motivo: 'ERROR_API', detalle: (typeof res?.error === 'string' ? res.error : res?.error?.message) || 'no pudimos hablar con la API ahora' };
}

/**
 * UNA OBSERVACION PENDIENTE, con los nombres que usa la vista.
 *
 * `confianza` se deja en NULL cuando la API no la manda, y no se convierte en
 * 0. Es la diferencia entre "el asistente no declaro cuanta confianza tiene" y
 * "no tiene ninguna", y la pantalla las dice distinto. Un cero inventado en la
 * columna que se mira para decidir sobre una persona seria de lo peor que
 * podria poner aqui.
 */
function observacionDe(o) {
  const c = pick(o, 'confidence', 'Confidence');
  return {
    id: texto(pick(o, 'id', 'Id')),
    tipo: texto(pick(o, 'kind', 'Kind')),
    metrica: texto(pick(o, 'metric', 'Metric')),
    tema: texto(pick(o, 'topicCode', 'topic_code', 'TopicCode')),
    confianza: c === null ? null : Number(c),
    valor: texto(pick(o, 'value', 'Value')),
    agente: texto(pick(o, 'agentCode', 'agent_code', 'AgentCode')),
    cuando: texto(pick(o, 'observedAt', 'observed_at', 'ObservedAt'))
  };
}

function perfilDe(p) {
  if (!p) return null;
  return {
    sujeto: texto(pick(p, 'subjectId', 'subject_id', 'SubjectId')),
    programa: texto(pick(p, 'programCode', 'program_code', 'ProgramCode')),
    nivel: pick(p, 'levelCurrent', 'level_current', 'LevelCurrent'),
    tema: texto(pick(p, 'currentTopicCode', 'current_topic_code', 'CurrentTopicCode')),
    dominados: lista(pick(p, 'topicsMastered', 'topics_mastered', 'TopicsMastered')).map(texto),
    version: Number(pick(p, 'version', 'Version') ?? 0),
    validadoPor: texto(pick(p, 'validatedBy', 'validated_by', 'ValidatedBy')),
    validadoEl: texto(pick(p, 'validatedAt', 'validated_at', 'ValidatedAt'))
  };
}

function sesionDe(s) {
  return {
    id: texto(pick(s, 'id', 'Id')),
    estado: texto(pick(s, 'status', 'Status')),
    desde: texto(pick(s, 'startedAt', 'started_at', 'StartedAt')),
    hasta: texto(pick(s, 'endedAt', 'ended_at', 'EndedAt')),
    minutos: pick(s, 'durationMin', 'duration_min', 'DurationMin')
  };
}

export const LearningProfileApiClient = {
  /**
   * QUIEN SOY EN APRENDIZAJE: mi sujeto y mis programas (CTX-P2).
   *
   * Existe porque `getProfile` pide dos datos que la pantalla no tenia de donde
   * sacar: el UUID del sujeto y el codigo del programa. Hasta hoy la vista de
   * accesos los pedia ESCRITOS A MANO, y su propio comentario lo reconocia:
   * "No hay lista de personas todavia: la API no publica un endpoint que las
   * enumere". Pegar un UUID a mano no es una pantalla, es un formulario de
   * consola.
   *
   * COMO SE RESUELVE, leido en NexoAPI origin/main (no en la rama que el repo
   * tenia en checkout, que era argy-api/sec-public-tres-puertas):
   * SubjectResolver.ResolverPropioAsync toma el email del JWT del panel y hace
   * users.email -> users.user_id -> context_subjects.id. O sea que para MI
   * PROPIA fila no hace falta que nadie escriba nada.
   *
   * LO QUE ESTO NO RESUELVE, y se dice de frente: el sujeto de OTRA persona.
   * Medido sin credenciales, con control positivo (/api/v1/bitacora -> 200) y
   * dos controles negativos de la misma forma (-> 404):
   *     GET /api/v1/learning/subjects      -> 404   (no existe)
   *     GET /api/v1/learning/observations  -> 404   (no existe)
   *     GET /api/v1/learning/sessions      -> 405   (existe, otro verbo)
   * Mientras no haya un endpoint que enumere sujetos, la pestana de OTRO
   * usuario dice por que no puede mostrar nada en vez de quedarse muda.
   *
   * @returns {{ok:true, sujetoId, sujetoNombre, programas:string[]}|{ok:false, motivo, detalle}}
   */
  async whoami() {
    let res;
    try { res = await ApiBase.get(BASE + '/whoami'); }
    catch (err) { return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' }; }
    if (!res.ok) return { ok: false, ...motivoDe(res) };
    const d = res.data?.data ?? res.data ?? {};
    const suj = pick(d, 'subject', 'Subject') ?? {};
    return {
      ok: true,
      sujetoId: texto(pick(suj, 'id', 'Id', 'subjectId', 'SubjectId')),
      sujetoNombre: texto(pick(suj, 'displayName', 'DisplayName', 'nombre')),
      // El DTO los declara con JsonPropertyName explicito, asi que el casing no
      // es una suposicion; se toleran los dos igual porque cuesta una linea.
      programas: lista(pick(d, 'programs', 'Programs')).map(texto).filter(Boolean)
    };
  },

  /**
   * El perfil de una persona en un programa, con lo que espera decision.
   * @returns {{ok:true, perfil, pendientes, sesiones}|{ok:false, motivo, detalle}}
   */
  async getProfile({ sujeto, programa }) {
    const s = texto(sujeto).trim();
    const p = texto(programa).trim();
    // Se valida ANTES de gastar el viaje, y se dice QUE falta -- no "revisa los
    // datos", que obliga a adivinar cual.
    if (!s) return { ok: false, motivo: 'FALTA_SUJETO', detalle: 'falta el id de la persona (subject id)' };
    if (!p) return { ok: false, motivo: 'FALTA_PROGRAMA', detalle: 'falta el codigo del programa' };

    const ruta = BASE + '/profile?subject=' + encodeURIComponent(s) + '&program=' + encodeURIComponent(p);
    let res;
    try { res = await ApiBase.get(ruta); }
    catch (err) { return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' }; }
    if (!res.ok) return { ok: false, ...motivoDe(res) };

    // La API envuelve en {success, data}. ApiBase ya puede desenvolver una capa,
    // asi que se toleran las dos formas: cuesta una linea y protege.
    const d = res.data?.data ?? res.data ?? {};
    return {
      ok: true,
      perfil: perfilDe(pick(d, 'profile', 'Profile')),
      pendientes: lista(pick(d, 'pending', 'Pending')).map(observacionDe),
      sesiones: lista(pick(d, 'sessions', 'Sessions')).map(sesionDe)
    };
  },

  /**
   * La decision de la persona sobre lo que el asistente propuso.
   *
   * EL CUERPO SE ARMA AQUI Y NO EN LA VISTA: un solo sitio conoce el contrato.
   * Las claves van en camelCase porque los DTO no llevan JsonPropertyName y
   * Program.cs no fija politica; ademas ASP.NET liga sin distinguir mayusculas
   * pero NO quita guiones bajos, asi que un snake_case no ligaria con nada y el
   * campo llegaria vacio sin avisar. El smoke lo verifica clave por clave.
   */
  async validate({ sujeto, programa, decisiones }) {
    const s = texto(sujeto).trim();
    const p = texto(programa).trim();
    if (!s) return { ok: false, motivo: 'FALTA_SUJETO', detalle: 'falta el id de la persona' };
    if (!p) return { ok: false, motivo: 'FALTA_PROGRAMA', detalle: 'falta el codigo del programa' };
    const decs = lista(decisiones).filter((d) => d && d.id && (d.decision === 'accept' || d.decision === 'reject'));
    if (!decs.length) return { ok: false, motivo: 'SIN_DECISIONES', detalle: 'no marcaste ninguna observacion' };

    const cuerpo = {
      decisions: decs.map((d) => {
        const fila = { observationId: d.id, decision: d.decision };
        // La nota solo viaja si la hay: mandar note vacia ensucia el rastro con
        // notas que despues alguien tiene que interpretar.
        const nota = texto(d.nota).trim();
        if (nota) fila.note = nota;
        return fila;
      })
    };

    const ruta = BASE + '/profiles/' + encodeURIComponent(s) + '/' + encodeURIComponent(p) + '/validate';
    let res;
    try { res = await ApiBase.post(ruta, cuerpo); }
    catch (err) { return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' }; }
    if (!res.ok) return { ok: false, ...motivoDe(res) };

    const d = res.data?.data ?? res.data ?? {};
    return {
      ok: true,
      aceptadas: Number(pick(d, 'aceptadas', 'Aceptadas') ?? 0),
      rechazadas: Number(pick(d, 'rechazadas', 'Rechazadas') ?? 0),
      superseded: Number(pick(d, 'superseded', 'Superseded') ?? 0),
      versionAnterior: Number(pick(d, 'versionAnterior', 'VersionAnterior') ?? 0),
      versionNueva: Number(pick(d, 'versionNueva', 'VersionNueva') ?? 0),
      perfil: perfilDe(pick(d, 'profile', 'Profile'))
    };
  }
};
